import React, { createElement } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OrderProvider } from './contexts/OrderContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ActivityLogProvider } from './contexts/ActivityLogContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import { BalanceProvider } from './contexts/BalanceContext';
import { ToastProvider } from './components/ui/Toast';
import { Header } from './components/Header';
import { TopNav } from './components/TopNav';
import { BottomNav } from './components/BottomNav';
import { Footer } from './components/Footer';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminLayout } from './components/AdminLayout';
import { OrdersDashboard } from './pages/OrdersDashboard';
import { LabelControl } from './pages/LabelControl';
import { ProductionView } from './pages/ProductionView';
import { MenuView } from './pages/MenuView';
import { CompanySettings } from './pages/CompanySettings';
import { EmployeeList } from './pages/EmployeeList';
import { EmployeeOrderPage } from './pages/EmployeeOrderPage';
import { MyOrdersPage } from './pages/MyOrdersPage';
import { EmployeeProfilePage } from './pages/EmployeeProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { ReportsPage } from './pages/ReportsPage';
import { ActivityLogPage } from './pages/ActivityLogPage';
import { ClaimsPage } from './pages/ClaimsPage';
import { PromotionsPage } from './pages/PromotionsPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { AdminChatPage } from './pages/AdminChatPage';
import { EmployeeChatPage } from './pages/EmployeeChatPage';
import { CompanyConsumptionPage } from './pages/CompanyConsumptionPage';
import { DemoRequestsPage } from './pages/DemoRequestsPage';
import { ManagerEmployeesPage } from './pages/ManagerEmployeesPage';
import { DeliveryDashboard } from './pages/DeliveryDashboard';
import { AssignmentsPage } from './pages/AssignmentsPage';
import { ForcePasswordChangeModal } from './components/ForcePasswordChangeModal';
import { SubsidyManagementPage } from './pages/SubsidyManagementPage';
import { AdminGuidePage } from './pages/AdminGuidePage';
(function setFavicon() {
  const url = 'https://cdn.magicpatterns.com/uploads/gGanbrXR1i8X3GiqD9Tci5/MR_Disenos_Instagram_(6).png';
  let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = url;
  // Also set apple-touch-icon for iOS
  let apple: HTMLLinkElement | null = document.querySelector("link[rel='apple-touch-icon']");
  if (!apple) {
    apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    document.head.appendChild(apple);
  }
  apple.href = url;
})();
// Wrapper that only shows the password change modal on authenticated routes
function RouteAwarePasswordModal() {
  const location = useLocation();
  const isPublicRoute = ['/', '/landing', '/login', '/terms', '/privacy'].includes(location.pathname);
  if (isPublicRoute) return null;
  return <ForcePasswordChangeModal data-id="element-0" />;
}
function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const isChatPage = location.pathname.includes('/chat');
  return <div className="flex flex-col h-[100dvh] bg-gray-50 font-sans text-gray-900 transition-colors" data-id="element-1">
      {!isChatPage && <Header data-id="element-2" />}
      {!isChatPage && <TopNav data-id="element-3" />}
      <main className="flex-1 min-h-0 overflow-y-auto" data-id="element-4">
        {children}
        {/* Spacer so content doesn't hide behind the fixed BottomNav on mobile */}
        {!isChatPage && <div className="h-16 md:h-0" data-id="element-5" />}
      </main>
      {!isChatPage && <BottomNav data-id="element-6" />}
    </div>;
}
export function App() {
  return <ThemeProvider data-id="element-7">
      <ToastProvider data-id="element-8">
        <AuthProvider data-id="element-9">
          <OrderProvider data-id="element-10">
            <NotificationProvider data-id="element-11">
              <ChatProvider data-id="element-12">
                <BalanceProvider data-id="element-13">
                  <ActivityLogProvider data-id="element-14">
                    <Router data-id="element-15">
                      <RouteAwarePasswordModal data-id="element-16" />
                      <Routes data-id="element-17">
                        <Route path="/" element={<LandingPage data-id="element-19" />} data-id="element-18" />
                        <Route path="/landing" element={<LandingPage data-id="element-21" />} data-id="element-20" />
                        <Route path="/login" element={<LoginPage data-id="element-23" />} data-id="element-22" />
                        <Route path="/terms" element={<TermsPage data-id="element-25" />} data-id="element-24" />
                        <Route path="/privacy" element={<PrivacyPage data-id="element-27" />} data-id="element-26" />

                        {/* Admin Routes */}
                        <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin']} data-id="element-29">
                              <AdminLayout data-id="element-30">
                                <Routes data-id="element-31">
                                  <Route path="orders" element={<OrdersDashboard data-id="element-33" />} data-id="element-32" />
                                  <Route path="chat" element={<AdminChatPage data-id="element-35" />} data-id="element-34" />
                                  <Route path="production" element={<ProductionView data-id="element-37" />} data-id="element-36" />
                                  <Route path="labels" element={<LabelControl data-id="element-39" />} data-id="element-38" />
                                  <Route path="assignments" element={<AssignmentsPage data-id="element-41" />} data-id="element-40" />
                                  <Route path="companies" element={<CompanySettings data-id="element-43" />} data-id="element-42" />
                                  <Route path="employees" element={<EmployeeList data-id="element-45" />} data-id="element-44" />
                                  <Route path="menu" element={<MenuView data-id="element-47" />} data-id="element-46" />
                                  <Route path="promotions" element={<PromotionsPage data-id="element-49" />} data-id="element-48" />
                                  <Route path="reports" element={<ReportsPage data-id="element-51" />} data-id="element-50" />
                                  <Route path="claims" element={<ClaimsPage data-id="element-53" />} data-id="element-52" />
                                  <Route path="demo-requests" element={<DemoRequestsPage data-id="element-55" />} data-id="element-54" />
                                  <Route path="activity-log" element={<ActivityLogPage data-id="element-57" />} data-id="element-56" />
                                  <Route path="settings" element={<SettingsPage data-id="element-59" />} data-id="element-58" />
                                  <Route path="delivery" element={<DeliveryDashboard data-id="element-61" />} data-id="element-60" />
                                  <Route path="subsidies" element={<SubsidyManagementPage data-id="element-63" />} data-id="element-62" />
                                  <Route path="guide" element={<AdminGuidePage data-id="element-65" />} data-id="element-64" />
                                  <Route path="*" element={<Navigate to="orders" replace data-id="element-67" />} data-id="element-66" />
                                </Routes>
                              </AdminLayout>
                            </ProtectedRoute>} data-id="element-28" />

                        {/* Employee Routes */}
                        <Route path="/employee/*" element={<ProtectedRoute allowedRoles={['employee']} data-id="element-69">
                              <Layout data-id="element-70">
                                <Routes data-id="element-71">
                                  <Route path="order" element={<EmployeeOrderPage data-id="element-73" />} data-id="element-72" />
                                  <Route path="my-orders" element={<MyOrdersPage data-id="element-75" />} data-id="element-74" />
                                  <Route path="company-report" element={<CompanyConsumptionPage data-id="element-77" />} data-id="element-76" />
                                  <Route path="employees" element={<ManagerEmployeesPage data-id="element-79" />} data-id="element-78" />
                                  <Route path="profile" element={<EmployeeProfilePage data-id="element-81" />} data-id="element-80" />
                                  <Route path="chat" element={<EmployeeChatPage data-id="element-83" />} data-id="element-82" />
                                  <Route path="*" element={<Navigate to="order" replace data-id="element-85" />} data-id="element-84" />
                                </Routes>
                              </Layout>
                            </ProtectedRoute>} data-id="element-68" />

                        <Route path="*" element={<Navigate to="/login" replace data-id="element-87" />} data-id="element-86" />
                      </Routes>
                    </Router>
                  </ActivityLogProvider>
                </BalanceProvider>
              </ChatProvider>
            </NotificationProvider>
          </OrderProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>;
}